import {
  Icons,
  Input,
  Label,
  MultiSelect,
  Select,
  TextFieldGroup,
  Tooltip
} from "@texada/ui";
import { capitalize } from "lodash";
import * as React from "react";
import "./CustomizedFields.scss";

export enum FIELD_TYPE {
  TEXT = "T",
  NUMBER = "N",
  SELECT = "S",
  MULTISELECT = "M",
  DATE = "D",
  CURRENCY = "C"
}

export const mapFieldLabel = {
  [FIELD_TYPE.TEXT]: "text",
  [FIELD_TYPE.NUMBER]: "number",
  [FIELD_TYPE.SELECT]: "dropdown",
  [FIELD_TYPE.MULTISELECT]: "multselect",
  [FIELD_TYPE.DATE]: "date",
  [FIELD_TYPE.CURRENCY]: "currency"
};

const mapFieldType = {
  [FIELD_TYPE.TEXT]: Input,
  [FIELD_TYPE.NUMBER]: Input,
  [FIELD_TYPE.SELECT]: Select,
  [FIELD_TYPE.MULTISELECT]: MultiSelect,
  [FIELD_TYPE.CURRENCY]: Input
};

export const mapDefaultValue = {
  [FIELD_TYPE.TEXT]: "",
  [FIELD_TYPE.NUMBER]: "",
  [FIELD_TYPE.SELECT]: null,
  [FIELD_TYPE.MULTISELECT]: [],
  [FIELD_TYPE.CURRENCY]: ""
};

const mapFieldProps = ({ value }) => {
  return {
    [FIELD_TYPE.TEXT]: {
      fluid: true,
      type: "text",
      inputProps: {
        autoComplete: "off"
      }
    },
    [FIELD_TYPE.NUMBER]: {
      fluid: true,
      type: "number",
      numberControl: true,
      inputProps: {
        min: "0",
        onKeyDown: e => {
          if (e.key === "-") {
            e.preventDefault();
            e.stopPropagation();
          }
        }
      }
    },
    [FIELD_TYPE.SELECT]: {},
    [FIELD_TYPE.MULTISELECT]: {
      selectedItems: value
    },
    [FIELD_TYPE.CURRENCY]: {
      fluid: true,
      type: "number",
      numberControl: false,
      inputProps: {
        min: "0",
        onKeyDown: e => {
          if (e.key === "-") {
            e.preventDefault();
            e.stopPropagation();
          }
        }
      }
    }
  };
};

interface ICustomizedFields<T, M, N> {
  fieldName: M extends string ? string : string;
  fieldType: FIELD_TYPE;
  placeholder?: T extends FIELD_TYPE.TEXT ? string : "";
  tooltip?: string;
  required?: boolean;
  options?: N[];
  value: (typeof mapDefaultValue)[keyof typeof mapDefaultValue];
  onChange: ({
    fieldName,
    value
  }: {
    fieldName: string;
    value: any;
  }) => any | void;
  error?: string;
  disabled?: boolean;
  numberOfDigits?: number;
  numberOfDecimals?: number;
}

export interface ICustomizedFieldsForwardRef {}

const CustomizedFields = React.forwardRef(
  <T, M, N>(
    {
      fieldName,
      fieldType,
      placeholder,
      tooltip,
      options,
      required,
      onChange,
      error,
      value,
      disabled,
      numberOfDigits,
      numberOfDecimals
    }: ICustomizedFields<T, M, N>,
    ref: ICustomizedFieldsForwardRef
  ) => {
    const inputFieldName = fieldName;
    const Field = mapFieldType[fieldType];

    const handleChange = event => {
      const regular = () => {
        const eventTargetValue = event.target.value;
        if ([FIELD_TYPE.NUMBER, FIELD_TYPE.CURRENCY].includes(fieldType)) {
          const numberArray = event.target.value.toString().split(".")
          if(Number(numberArray[0]) > 0 && numberArray[0]?.length > numberOfDigits ){
            onChange({
              fieldName: inputFieldName,
              value: value
            });
            return
          }
          if (Number(numberArray[1]) > 0 && numberArray[1]?.length > numberOfDecimals){
            onChange({
              fieldName: inputFieldName,
              value: value
            });
            return
          }
        }

        if (onChange) {
          onChange({
            fieldName: inputFieldName,
            value: eventTargetValue
          });
        }
      };

      const mapChange = {
        [FIELD_TYPE.MULTISELECT]: () => {
          onChange({
            fieldName: inputFieldName,
            value: event
          });
        }
      };

      return fieldType in mapChange ? mapChange[fieldType]() : regular();
    };

    React.useImperativeHandle(
      ref,
      () => {
        return {};
      },
      []
    );

    const commonProps = {
      required,
      name: inputFieldName,
      value,
      disabled,
      ...(placeholder ? { placeholder } : {}),
      ...(error ? { error } : {})
    };

    const inputProps = {
      ...commonProps,
      ...mapFieldProps({ value })[fieldType],
      ...(options ? { options } : {}),
      onChange: handleChange
    };

    return (
      <>
        <div className="customized-field-container">
          <div style={{ marginRight: 12 }}>
            <Label required={required}>{fieldName}</Label>
          </div>
          {tooltip ? (
            <Tooltip label={tooltip}>
              <Icons.Widgets />
            </Tooltip>
          ) : null}
        </div>
        <Field {...inputProps} />
      </>
    );
  }
);

export default CustomizedFields;
